import React, { useState } from 'react';
import Modal from './Modal';
import { uploadFile, cdnUrl, isUploadConfigured } from '../data/uploadService';
import { addMovie, buildMovieKey, updateMovie, deleteMovie } from '../data/firestore';

let songSeq = 0;
const newSong = () => ({ key: `s${Date.now()}${songSeq++}`, label: '', file: null });

const STEPS = ['Details', 'Video', 'Music', 'Review'];

function readMediaDuration(file) {
  return new Promise((resolve) => {
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      resolve(Number.isFinite(el.duration) ? el.duration : 0);
      URL.revokeObjectURL(el.src);
    };
    el.onerror = () => resolve(0);
    el.src = URL.createObjectURL(file);
  });
}

const fmtDur = (s) => {
  if (!s) return '';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};
const fmtSize = (b) => {
  if (!b) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};

export default function MovieUploadModal({ onClose }) {
  const configured = isUploadConfigured();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    title: '',
    location: '',
    year: new Date().getFullYear(),
    synopsis: '',
    categories: 'Surf',
    thumbUrl: '',
    featured: false,
  });
  const [videoFile, setVideoFile] = useState(null);
  const [videoDur, setVideoDur] = useState(0);
  const [drag, setDrag] = useState(false);
  const [hasOriginalAudio, setHasOriginalAudio] = useState(true);
  const [songs, setSongs] = useState([]);
  const [defaultAudio, setDefaultAudio] = useState('original');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({});
  const [error, setError] = useState(null);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setProg = (label, p) => setProgress((prev) => ({ ...prev, [label]: p }));
  const pct = (label) => Math.round((progress[label] || 0) * 100);

  const pickVideo = async (file) => {
    if (!file) return;
    setVideoFile(file);
    setVideoDur(0);
    setVideoDur(await readMediaDuration(file));
  };

  const addSongRow = () => setSongs((s) => [...s, newSong()]);
  const removeSong = (key) => setSongs((s) => s.filter((x) => x.key !== key));
  const updateSong = (key, patch) =>
    setSongs((s) => s.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const audioOptions = [
    ...(hasOriginalAudio ? [{ value: 'original', label: 'Original audio (from video)' }] : []),
    ...songs.map((s, i) => ({ value: s.key, label: s.label || `Song ${i + 1}` })),
  ];

  const stepValid = [
    !!form.title.trim(),
    !!videoFile,
    songs.every((s) => s.file),
    configured && !!form.title.trim() && !!videoFile && songs.every((s) => s.file),
  ];

  const save = async () => {
    setError(null);
    setBusy(true);
    let movieId;
    try {
      const categories = form.categories.split(',').map((s) => s.trim()).filter(Boolean);
      movieId = await addMovie({
        title: form.title.trim(),
        location: form.location,
        year: Number(form.year) || new Date().getFullYear(),
        synopsis: form.synopsis,
        categories: categories.length ? categories : ['Surf'],
        thumbUrl: form.thumbUrl,
        featured: form.featured,
        streamType: 'mp4',
        hasOriginalAudio,
        status: 'uploading',
      });

      const durationSec = videoDur || (await readMediaDuration(videoFile));

      const videoKey = buildMovieKey(movieId, '', videoFile.name);
      await uploadFile(videoFile, {
        key: videoKey,
        contentType: videoFile.type || 'video/mp4',
        bucket: 'movies',
        onProgress: (p) => setProg('video', p),
      });

      const uploadedSongs = [];
      for (let i = 0; i < songs.length; i += 1) {
        const s = songs[i];
        const songKey = buildMovieKey(movieId, 'songs/', s.file.name);
        const label = s.label.trim() || `Song ${i + 1}`;
        // eslint-disable-next-line no-await-in-loop
        await uploadFile(s.file, {
          key: songKey,
          contentType: s.file.type || 'audio/mpeg',
          bucket: 'movies',
          onProgress: (p) => setProg(`song:${s.key}`, p),
        });
        uploadedSongs.push({
          id: s.key,
          source: 'upload',
          label,
          url: cdnUrl(songKey),
          libraryId: null,
          trimStart: 0,
          trimEnd: 0,
          movieOffset: 0,
          volume: 1,
        });
      }

      let resolvedDefault = defaultAudio;
      const validIds = new Set(uploadedSongs.map((s) => s.id));
      if (resolvedDefault !== 'original' && !validIds.has(resolvedDefault)) {
        resolvedDefault = hasOriginalAudio ? 'original' : uploadedSongs[0]?.id || 'original';
      }
      if (resolvedDefault === 'original' && !hasOriginalAudio) {
        resolvedDefault = uploadedSongs[0]?.id || 'original';
      }

      await updateMovie(movieId, {
        videoUrl: cdnUrl(videoKey),
        durationSec,
        songs: uploadedSongs,
        defaultAudio: resolvedDefault,
        status: 'ready',
      });

      onClose();
    } catch (err) {
      setError(err.message || String(err));
      if (movieId) await deleteMovie(movieId).catch(() => {});
      setBusy(false);
    }
  };

  return (
    <Modal title="New movie" onClose={busy ? () => {} : onClose} maxWidth={620}>
      {!configured && (
        <div className="cs-empty" style={{ marginBottom: 16, padding: 18 }}>
          Upload backend not configured (REACT_APP_UPLOAD_API).
        </div>
      )}

      <div className="cs-steps">
        {STEPS.map((label, i) => (
          <div
            key={label}
            className={`cs-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
          >
            <span className="cs-step-num">{i < step ? '✓' : i + 1}</span>
            {label}
          </div>
        ))}
      </div>

      {/* STEP 0 — DETAILS */}
      {step === 0 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12 }}>
            <div className="cs-field" style={{ margin: 0 }}>
              <label>Title *</label>
              <input className="cs-input" value={form.title} onChange={set('title')} autoFocus />
            </div>
            <div className="cs-field" style={{ margin: 0 }}>
              <label>Year</label>
              <input className="cs-input" type="number" value={form.year} onChange={set('year')} />
            </div>
          </div>
          <div className="cs-field" style={{ marginTop: 12 }}>
            <label>Location</label>
            <input className="cs-input" value={form.location} onChange={set('location')} />
          </div>
          <div className="cs-field">
            <label>Synopsis</label>
            <textarea
              className="cs-input"
              rows={3}
              value={form.synopsis}
              onChange={set('synopsis')}
              style={{ resize: 'vertical' }}
            />
          </div>
          <div className="cs-field">
            <label>Categories (comma-separated)</label>
            <input className="cs-input" value={form.categories} onChange={set('categories')} />
          </div>
          <div className="cs-field">
            <label>Thumbnail image URL (optional)</label>
            <input className="cs-input" value={form.thumbUrl} onChange={set('thumbUrl')} />
          </div>
          <label style={{ color: '#ccc', fontSize: 14 }}>
            <input type="checkbox" checked={form.featured} onChange={set('featured')} /> Feature on
            the home hero banner
          </label>
        </div>
      )}

      {/* STEP 1 — VIDEO */}
      {step === 1 && (
        <div>
          <label
            className={`cs-filepick ${drag ? 'drag' : ''} ${videoFile ? 'set' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              pickVideo(e.dataTransfer.files?.[0]);
            }}
          >
            {videoFile ? (
              <div>
                <div style={{ fontWeight: 600 }}>{videoFile.name}</div>
                <div className="cs-help">
                  {fmtSize(videoFile.size)}
                  {videoDur ? ` · ${fmtDur(videoDur)}` : ''} · click to change
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 28, marginBottom: 6 }}>⬆</div>
                Drag a video here, or click to choose
                <div className="cs-help">MP4 / MOV — this is the film people will watch</div>
              </div>
            )}
            <input
              type="file"
              accept="video/*"
              hidden
              onChange={(e) => pickVideo(e.target.files?.[0])}
            />
          </label>
          <label style={{ color: '#ccc', fontSize: 14, display: 'block', marginTop: 16 }}>
            <input
              type="checkbox"
              checked={hasOriginalAudio}
              onChange={(e) => setHasOriginalAudio(e.target.checked)}
            />{' '}
            This video already has audio/music in it
          </label>
        </div>
      )}

      {/* STEP 2 — MUSIC */}
      {step === 2 && (
        <div>
          <p className="cs-section-sub" style={{ marginTop: 0 }}>
            Optional. Add MP3s as alternate soundtracks — you can trim and align them later in the
            editor.
          </p>
          {songs.map((s, i) => (
            <div key={s.key} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input
                className="cs-input"
                placeholder={`Song ${i + 1} label`}
                value={s.label}
                style={{ flex: '0 0 180px' }}
                onChange={(e) => updateSong(s.key, { label: e.target.value })}
              />
              <input
                type="file"
                accept="audio/*"
                style={{ flex: 1 }}
                onChange={(e) => updateSong(s.key, { file: e.target.files?.[0] || null })}
              />
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() => removeSong(s.key)}
                type="button"
              >
                ✕
              </button>
            </div>
          ))}
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={addSongRow} type="button">
            ＋ Add song
          </button>

          {audioOptions.length > 0 && (
            <div className="cs-field" style={{ marginTop: 18 }}>
              <label>Default soundtrack</label>
              {audioOptions.map((o) => (
                <label key={o.value} style={{ display: 'block', fontWeight: 400, marginBottom: 4 }}>
                  <input
                    type="radio"
                    name="defaultAudio"
                    checked={defaultAudio === o.value}
                    onChange={() => setDefaultAudio(o.value)}
                  />{' '}
                  {o.label}
                </label>
              ))}
            </div>
          )}
          {!songs.some((s) => s.file) && (
            <div className="cs-help" style={{ marginTop: 12 }}>
              No alternate songs — the video’s original audio will be used.
            </div>
          )}
        </div>
      )}

      {/* STEP 3 — REVIEW */}
      {step === 3 && (
        <div>
          {busy ? (
            <div>
              <div className="cs-upload-item">
                <span style={{ flex: '0 0 120px' }}>Video</span>
                <div className="cs-progress">
                  <div style={{ width: `${pct('video')}%` }} />
                </div>
                <span style={{ width: 40, textAlign: 'right' }}>{pct('video')}%</span>
              </div>
              {songs.map((s, i) => (
                <div key={s.key} className="cs-upload-item">
                  <span style={{ flex: '0 0 120px' }}>{s.label || `Song ${i + 1}`}</span>
                  <div className="cs-progress">
                    <div style={{ width: `${pct(`song:${s.key}`)}%` }} />
                  </div>
                  <span style={{ width: 40, textAlign: 'right' }}>{pct(`song:${s.key}`)}%</span>
                </div>
              ))}
              <div className="cs-help" style={{ marginTop: 12 }}>
                Uploading… keep this window open until it finishes.
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 14, lineHeight: 1.8 }}>
              <div>
                <strong>{form.title || 'Untitled'}</strong> · {form.year}
                {form.location ? ` · ${form.location}` : ''}
              </div>
              <div style={{ color: 'var(--muted)' }}>
                Video: {videoFile ? `${videoFile.name} (${fmtSize(videoFile.size)})` : '—'}
              </div>
              <div style={{ color: 'var(--muted)' }}>
                Soundtracks: {songs.filter((s) => s.file).length} added
                {hasOriginalAudio ? ' + original audio' : ''}
              </div>
              <div style={{ color: 'var(--muted)' }}>
                Default:{' '}
                {audioOptions.find((o) => o.value === defaultAudio)?.label ||
                  audioOptions[0]?.label ||
                  'Original audio'}
              </div>
            </div>
          )}
          {error && <div className="cs-empty" style={{ marginTop: 12 }}>{error}</div>}
        </div>
      )}

      {/* FOOTER */}
      <div className="cs-wizard-foot">
        <button
          className="cs-btn cs-btn-ghost"
          disabled={busy || step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          Back
        </button>
        {step < STEPS.length - 1 ? (
          <button
            className="cs-btn cs-btn-primary"
            disabled={!stepValid[step]}
            onClick={() => setStep((s) => s + 1)}
          >
            Next
          </button>
        ) : (
          <button className="cs-btn cs-btn-primary" disabled={!stepValid[3] || busy} onClick={save}>
            {busy ? 'Uploading…' : 'Upload movie'}
          </button>
        )}
      </div>
    </Modal>
  );
}
